import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { entryAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Download, Printer, FileSpreadsheet, FileText, Eye, Loader2, MessageCircle } from 'lucide-react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// HTML sanitization helper to prevent XSS
const sanitizeHTML = (str) => {
  if (typeof str !== 'string') return str;
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
};

export default function PreviewPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [selectedEntryId, setSelectedEntryId] = useState(null);
  const [reportData, setReportData] = useState(null);
  const printRef = useRef(null);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const response = await entryAPI.getAll();
      const data = response.data || [];
      setEntries(data);
      
      if (data.length > 0) {
        loadEntry(data[0]);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const loadEntry = (entry) => {
    setSelectedEntryId(entry.id);
    const blueprint = JSON.parse(entry.items || '[]');
    const students = JSON.parse(entry.students || '[]');
    
    setReportData({
      meta: {
        school: user?.school_name || '',
        udise: user?.udise_code || '',
        district: user?.district || '',
        block: user?.block || '',
        cluster: user?.cluster || '',
        class_name: entry.class_name,
        subject: entry.subject,
        exam: entry.exam,
        year: entry.year
      },
      blueprint,
      students
    });
  };

  const handleEntryChange = (entryId) => {
    const entry = entries.find(e => e.id === entryId);
    if (entry) {
      loadEntry(entry);
    }
  };

  // Group blueprint items by LO code with Written/Oral separation - sorted in ascending order
  const groupByLO = (blueprint) => {
    const loMap = {};
    const loOrder = [];
    
    blueprint.forEach(item => {
      if (!loMap[item.loCode]) {
        loMap[item.loCode] = { written: [], oral: [], writtenMarks: 0, oralMarks: 0 };
        loOrder.push(item.loCode);
      }
      if (item.section === 'Oral') {
        loMap[item.loCode].oral.push(item);
        loMap[item.loCode].oralMarks += item.marks || 0;
      } else {
        loMap[item.loCode].written.push(item);
        loMap[item.loCode].writtenMarks += item.marks || 0;
      }
    });
    
    // Sort LO codes in ascending order (alphanumeric)
    loOrder.sort((a, b) => {
      const aMatch = a.match(/([A-Za-z]+)(\d+)/);
      const bMatch = b.match(/([A-Za-z]+)(\d+)/);
      if (aMatch && bMatch) {
        if (aMatch[1] !== bMatch[1]) return aMatch[1].localeCompare(bMatch[1]);
        return parseInt(aMatch[2]) - parseInt(bMatch[2]);
      }
      return a.localeCompare(b);
    });
    
    return loOrder.map(lo => ({
      lo,
      written: loMap[lo].written,
      oral: loMap[lo].oral,
      writtenMarks: loMap[lo].writtenMarks,
      oralMarks: loMap[lo].oralMarks,
      totalMarks: loMap[lo].writtenMarks + loMap[lo].oralMarks,
      items: [...loMap[lo].written, ...loMap[lo].oral],
      writtenQLabels: loMap[lo].written.map(i => i.qLabel).join(', ') || '--',
      oralQLabels: loMap[lo].oral.map(i => i.qLabel).join(', ') || '--'
    }));
  };

  const getStudentLOTotal = (student, loItems) => {
    if (!student.marks) return 0;
    return loItems.reduce((sum, item) => {
      const mark = student.marks[item.id];
      return sum + (typeof mark === 'number' ? mark : 0);
    }, 0);
  };

  const getStudentWrittenTotal = (student, loGroup) => {
    if (!student.marks) return 0;
    return loGroup.written.reduce((sum, item) => {
      const mark = student.marks[item.id];
      return sum + (typeof mark === 'number' ? mark : 0);
    }, 0);
  };

  const getStudentOralTotal = (student, loGroup) => {
    if (!student.marks) return 0;
    return loGroup.oral.reduce((sum, item) => {
      const mark = student.marks[item.id];
      return sum + (typeof mark === 'number' ? mark : 0);
    }, 0);
  };

  const getLOClassTotal = (students, loItems) => {
    return students.reduce((sum, student) => {
      return sum + getStudentLOTotal(student, loItems);
    }, 0);
  };

  // Official PDF Export - A4 Landscape with proper structure
  const handleExportPDF = () => {
    if (!reportData || reportData.students.length === 0) {
      toast.error('No data to export');
      return;
    }

    const { meta, blueprint, students } = reportData;
    const loGroups = groupByLO(blueprint);
    
    // Create PDF in A4 Landscape
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });

    const pageWidth = doc.internal.pageSize.width;
    
    // Use Helvetica (closest to Calibri available in jsPDF)
    doc.setFont('helvetica');

    // Title Section - Centered
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('SCHOOL LEVEL COMPILATION FORMAT FOR ' + meta.exam + ' RESULT ANALYSIS ' + meta.year, pageWidth / 2, 12, { align: 'center' });
    
    doc.setFontSize(11);
    doc.text('FORMAT - A', pageWidth / 2, 18, { align: 'center' });

    // Meta information - Official layout with proper spacing
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const metaY = 26;
    
    // Row 1: District, Block, Cluster (evenly spaced)
    doc.text('DISTRICT:', 14, metaY);
    doc.setFont('helvetica', 'bold');
    doc.text(meta.district.toUpperCase(), 35, metaY);
    
    doc.setFont('helvetica', 'normal');
    doc.text('BLOCK:', 100, metaY);
    doc.setFont('helvetica', 'bold');
    doc.text(meta.block.toUpperCase(), 118, metaY);
    
    doc.setFont('helvetica', 'normal');
    doc.text('CLUSTER:', 180, metaY);
    doc.setFont('helvetica', 'bold');
    doc.text(meta.cluster.toUpperCase(), 202, metaY);
    
    // Row 2: School, Class, Subject
    doc.setFont('helvetica', 'normal');
    doc.text('SCHOOL:', 14, metaY + 6);
    doc.setFont('helvetica', 'bold');
    doc.text(meta.school.toUpperCase(), 35, metaY + 6);
    
    doc.setFont('helvetica', 'normal');
    doc.text('CLASS:', 180, metaY + 6);
    doc.setFont('helvetica', 'bold');
    doc.text(meta.class_name, 198, metaY + 6);
    
    doc.setFont('helvetica', 'normal');
    doc.text('SUBJECT:', 220, metaY + 6);
    doc.setFont('helvetica', 'bold');
    doc.text(meta.subject, 243, metaY + 6);

    // Build table headers - 3 rows: LO Code, Question Numbers, Total Marks
    const headers1 = ['SI.', 'Name of Student'];
    const headers2 = ['', ''];
    const headers3 = ['No.', ''];
    
    loGroups.forEach(group => {
      // Row 1: LO Code
      headers1.push(group.lo);
      // Row 2: Question Numbers (Written Qs, Oral Qs)
      const wQs = group.writtenQLabels !== '--' ? `W: ${group.writtenQLabels}` : '';
      const oQs = group.oralQLabels !== '--' ? `O: ${group.oralQLabels}` : '';
      headers2.push([wQs, oQs].filter(Boolean).join(' | ') || '--');
      // Row 3: Total Marks
      const wMarks = group.writtenMarks > 0 ? `W:${group.writtenMarks}` : '--';
      const oMarks = group.oralMarks > 0 ? `O:${group.oralMarks}` : '--';
      headers3.push(`${wMarks} | ${oMarks}`);
    });
    headers1.push('Grand');
    headers2.push('');
    headers3.push('Total');
    const totalPossible = loGroups.reduce((sum, g) => sum + g.totalMarks, 0);

    // Build data rows
    const data = students.map((student, idx) => {
      let grandTotal = 0;
      const row = [
        idx + 1,
        student.name
      ];
      
      loGroups.forEach(group => {
        const loTotal = getStudentLOTotal(student, group.items);
        grandTotal += loTotal;
        row.push(loTotal);
      });
      
      row.push(grandTotal);
      return row;
    });

    // Add Total Marks row
    const totalRow = ['', 'Total Marks Obtained'];
    let grandTotalObtained = 0;
    loGroups.forEach(group => {
      const loTotal = getLOClassTotal(students, group.items);
      grandTotalObtained += loTotal;
      totalRow.push(loTotal);
    });
    totalRow.push(grandTotalObtained);
    data.push(totalRow);

    // Add LO Wise % row
    const pctRow = ['', 'LO Wise Achievement %'];
    loGroups.forEach(group => {
      const total = getLOClassTotal(students, group.items);
      const maxPossible = group.totalMarks * students.length;
      const pct = maxPossible > 0 ? ((total / maxPossible) * 100).toFixed(1) : '0.0';
      pctRow.push(`${pct}%`);
    });
    const overallPct = (totalPossible * students.length) > 0 
      ? ((grandTotalObtained / (totalPossible * students.length)) * 100).toFixed(1)
      : '0.0';
    pctRow.push(`${overallPct}%`);
    data.push(pctRow);

    // Generate table with official styling - 3 header rows
    autoTable(doc, {
      startY: 40,
      head: [headers1, headers2, headers3],
      body: data,
      theme: 'grid',
      styles: { 
        fontSize: 7, 
        cellPadding: 1.5,
        font: 'helvetica',
        lineColor: [0, 0, 0],
        lineWidth: 0.3
      },
      headStyles: { 
        fillColor: [240, 240, 240], 
        textColor: [0, 0, 0], 
        fontStyle: 'bold',
        halign: 'center',
        lineWidth: 0.3
      },
      bodyStyles: {
        halign: 'center'
      },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center' },
        1: { cellWidth: 40, halign: 'left' },
      },
      didParseCell: (data) => {
        // Bold and highlight the last two rows (Total, %)
        if (data.row.index >= students.length) {
          data.cell.styles.fontStyle = 'bold';
          data.cell.styles.fillColor = [248, 248, 248];
        }
      }
    });

    // Add note at bottom left
    const finalY = doc.lastAutoTable.finalY + 5;
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.text('NB: W = Written | O = Oral | LO Wise Achievement % = (Total Obtained / Max Possible) × 100', 14, finalY);
    
    // Signature on RIGHT side, just below table
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('Signature of HM: _______________________', pageWidth - 14, finalY + 2, { align: 'right' });

    doc.save(`Format-A_${meta.class_name}_${meta.subject}_${meta.exam}_${meta.year}.pdf`);
    toast.success('Official Format-A PDF exported!');
  };

  // Generate LO-Wise Achievement Summary PDF
  const handleExportSummaryPDF = () => {
    if (!reportData || reportData.students.length === 0) {
      toast.error('No data to export');
      return;
    }

    try {
      const { meta, blueprint, students } = reportData;
      const loGroups = groupByLO(blueprint);
      
      if (loGroups.length === 0) {
        toast.error('No blueprint data available');
        return;
      }
      
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      doc.setFont('helvetica');

      // Title
      doc.setFontSize(14);
      doc.setFont('helvetica', 'bold');
      doc.text('LO-WISE ACHIEVEMENT SUMMARY', doc.internal.pageSize.width / 2, 20, { align: 'center' });
      
      doc.setFontSize(10);
      doc.text(`${meta.exam} ${meta.year} | Class ${meta.class_name} - ${meta.subject} | ${meta.school}`, doc.internal.pageSize.width / 2, 28, { align: 'center' });

      // Summary table
      const headers = [['SI', 'LO Code', 'Written', 'Oral', 'Allotted', 'Obtained', 'Max', 'Achievement %', 'Level']];
      
      let overallObtained = 0;
      let overallMax = 0;
      
      const data = loGroups.map((group, idx) => {
        const totalObtained = getLOClassTotal(students, group.items);
        const maxPossible = group.totalMarks * students.length;
        const pct = maxPossible > 0 ? (totalObtained / maxPossible) * 100 : 0;
        
        overallObtained += totalObtained;
        overallMax += maxPossible;
        
        let level = 'Low';
        if (pct >= 70) level = 'High';
        else if (pct >= 50) level = 'Medium';
        
        return [
          idx + 1,
          group.lo || 'N/A',
          group.writtenMarks || 0,
          group.oralMarks || 0,
          group.totalMarks,
          totalObtained,
          maxPossible,
          `${pct.toFixed(1)}%`,
          level
        ];
      });

      // Overall row
      const overallPct = overallMax > 0 ? (overallObtained / overallMax) * 100 : 0;
      data.push([
        '',
        'OVERALL',
        '',
        '',
        loGroups.reduce((s, g) => s + g.totalMarks, 0),
        overallObtained,
        overallMax,
        `${overallPct.toFixed(1)}%`,
        overallPct >= 70 ? 'High' : overallPct >= 50 ? 'Medium' : 'Low'
      ]);

      autoTable(doc, {
        startY: 38,
        head: headers,
        body: data,
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [30, 58, 110], textColor: 255, fontStyle: 'bold' },
        didParseCell: (cellData) => {
          if (cellData.row.index === loGroups.length) {
            cellData.cell.styles.fontStyle = 'bold';
            cellData.cell.styles.fillColor = [240, 240, 240];
          }
          // Color code levels
          if (cellData.column.index === 8 && cellData.row.index < loGroups.length + 1) {
            const level = cellData.cell.raw;
            if (level === 'High') cellData.cell.styles.textColor = [22, 163, 74];
            else if (level === 'Medium') cellData.cell.styles.textColor = [202, 138, 4];
            else if (level === 'Low') cellData.cell.styles.textColor = [220, 38, 38];
          }
        }
      });

      doc.save(`LO_Summary_${meta.class_name}_${meta.subject}_${meta.exam}_${meta.year}.pdf`);
      toast.success('LO Achievement Summary exported!');
    } catch (error) {
      console.error('Summary PDF Error:', error);
      toast.error('Failed to generate summary PDF');
    }
  };

  // Print functionality
  const handlePrint = () => {
    if (!reportData || reportData.students.length === 0) {
      toast.error('No data to print');
      return;
    }
    
    const { meta, blueprint, students } = reportData;
    const loGroups = groupByLO(blueprint);
    const totalPossibleMarks = loGroups.reduce((sum, g) => sum + g.totalMarks, 0);
    
    // Sanitize meta data to prevent XSS
    const safeMeta = {
      exam: sanitizeHTML(meta.exam),
      year: sanitizeHTML(meta.year),
      district: sanitizeHTML(meta.district),
      block: sanitizeHTML(meta.block),
      cluster: sanitizeHTML(meta.cluster),
      school: sanitizeHTML(meta.school),
      class_name: sanitizeHTML(meta.class_name),
      subject: sanitizeHTML(meta.subject)
    };
    
    // Build table rows HTML
    let tableRowsHTML = '';
    students.forEach((student, idx) => {
      let grandTotal = 0;
      let rowCells = `<td style="border:1px solid #000;padding:4px;text-align:center;">${idx + 1}</td>`;
      rowCells += `<td style="border:1px solid #000;padding:4px;text-align:left;">${sanitizeHTML(student.name)}</td>`;
      
      loGroups.forEach(group => {
        const loTotal = getStudentLOTotal(student, group.items);
        grandTotal += loTotal;
        rowCells += `<td style="border:1px solid #000;padding:4px;text-align:center;">${loTotal}</td>`;
      });
      rowCells += `<td style="border:1px solid #000;padding:4px;text-align:center;font-weight:bold;background:#fff8e1;">${grandTotal}</td>`;
      tableRowsHTML += `<tr>${rowCells}</tr>`;
    });
    
    // Total Marks Obtained row
    let totalRow = '<td colspan="2" style="border:1px solid #000;padding:4px;text-align:right;font-weight:bold;background:#f5f5f5;">Total Marks Obtained</td>';
    let grandTotalObtained = 0;
    loGroups.forEach(group => {
      const loTotal = getLOClassTotal(students, group.items);
      grandTotalObtained += loTotal;
      totalRow += `<td style="border:1px solid #000;padding:4px;text-align:center;font-weight:bold;background:#f5f5f5;">${loTotal}</td>`;
    });
    totalRow += `<td style="border:1px solid #000;padding:4px;text-align:center;font-weight:bold;background:#fff8e1;">${grandTotalObtained}</td>`;
    
    // LO Wise Achievement % row
    let pctRow = '<td colspan="2" style="border:1px solid #000;padding:4px;text-align:right;font-weight:bold;background:#e8f5e9;">LO Wise Achievement %</td>';
    loGroups.forEach(group => {
      const total = getLOClassTotal(students, group.items);
      const maxPossible = group.totalMarks * students.length;
      const pct = maxPossible > 0 ? ((total / maxPossible) * 100).toFixed(1) : '0.0';
      pctRow += `<td style="border:1px solid #000;padding:4px;text-align:center;font-weight:bold;background:#e8f5e9;color:#2e7d32;">${pct}%</td>`;
    });
    const overallPct = (totalPossibleMarks * students.length) > 0 
      ? ((grandTotalObtained / (totalPossibleMarks * students.length)) * 100).toFixed(1)
      : '0.0';
    pctRow += `<td style="border:1px solid #000;padding:4px;text-align:center;font-weight:bold;background:#e8f5e9;color:#2e7d32;">${overallPct}%</td>`;
    
    // Build header rows - 3 rows: LO Code, Question Numbers, Total Marks
    let headerRow1 = '<th style="border:1px solid #000;padding:4px;background:#f0f0f0;font-weight:bold;width:30px;" rowspan="3">SI.<br/>No.</th>';
    headerRow1 += '<th style="border:1px solid #000;padding:4px;background:#f0f0f0;font-weight:bold;width:150px;" rowspan="3">Name of Student</th>';
    loGroups.forEach(group => {
      headerRow1 += `<th style="border:1px solid #000;padding:4px;background:#f0f0f0;font-weight:bold;text-align:center;">${group.lo}</th>`;
    });
    headerRow1 += '<th style="border:1px solid #000;padding:4px;background:#f0f0f0;font-weight:bold;text-align:center;" rowspan="3">Grand<br/>Total</th>';
    
    // Row 2: Question Numbers
    let headerRow2 = '';
    loGroups.forEach(group => {
      const wQs = group.writtenQLabels !== '--' ? `W: ${group.writtenQLabels}` : '';
      const oQs = group.oralQLabels !== '--' ? `O: ${group.oralQLabels}` : '';
      const qText = [wQs, oQs].filter(Boolean).join('<br/>') || '--';
      headerRow2 += `<th style="border:1px solid #000;padding:2px;background:#f5f5f5;font-size:7px;font-weight:normal;text-align:center;">${qText}</th>`;
    });
    
    // Row 3: Total Marks
    let headerRow3 = '';
    loGroups.forEach(group => {
      const wMarks = group.writtenMarks > 0 ? `W:${group.writtenMarks}` : '--';
      const oMarks = group.oralMarks > 0 ? `O:${group.oralMarks}` : '--';
      headerRow3 += `<th style="border:1px solid #000;padding:2px;background:#f0f0f0;font-size:8px;font-weight:bold;text-align:center;">${wMarks} | ${oMarks}</th>`;
    });
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Format-A Report - ${safeMeta.class_name} ${safeMeta.subject}</title>
          <style>
            @page { size: A4 landscape; margin: 8mm; }
            * { box-sizing: border-box; }
            body { 
              font-family: Calibri, Arial, sans-serif; 
              font-size: 10px; 
              margin: 0;
              padding: 10mm;
              width: 100%;
            }
            .header { text-align: center; margin-bottom: 12px; }
            .header h1 { font-size: 13px; margin: 4px 0; font-weight: bold; }
            .header h2 { font-size: 11px; margin: 4px 0; font-weight: bold; }
            .meta-container { 
              display: table;
              width: 100%;
              margin-bottom: 10px;
            }
            .meta-row {
              display: table-row;
            }
            .meta-cell {
              display: table-cell;
              width: 33.33%;
              padding: 2px 5px;
              font-size: 10px;
            }
            .meta-cell span { font-weight: bold; }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              table-layout: fixed;
              font-size: 9px;
            }
            th, td { 
              border: 1px solid #000; 
              padding: 3px 4px; 
              text-align: center;
              vertical-align: middle;
            }
            th { background: #f0f0f0; font-weight: bold; }
            .footer { 
              margin-top: 10px; 
              font-size: 8px; 
              display: flex; 
              justify-content: space-between; 
              align-items: flex-start;
            }
            .footer-left { max-width: 60%; }
            .footer-right { text-align: right; }
            @media print {
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>SCHOOL LEVEL COMPILATION FORMAT FOR ${safeMeta.exam} RESULT ANALYSIS ${safeMeta.year}</h1>
            <h2>FORMAT - A</h2>
          </div>
          
          <div class="meta-container">
            <div class="meta-row">
              <div class="meta-cell"><span>DISTRICT:</span> ${safeMeta.district.toUpperCase()}</div>
              <div class="meta-cell"><span>BLOCK:</span> ${safeMeta.block.toUpperCase()}</div>
              <div class="meta-cell"><span>CLUSTER:</span> ${safeMeta.cluster.toUpperCase()}</div>
            </div>
            <div class="meta-row">
              <div class="meta-cell"><span>SCHOOL:</span> ${safeMeta.school.toUpperCase()}</div>
              <div class="meta-cell"><span>CLASS:</span> ${safeMeta.class_name}</div>
              <div class="meta-cell"><span>SUBJECT:</span> ${safeMeta.subject}</div>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>${headerRow1}</tr>
              <tr>${headerRow2}</tr>
              <tr>${headerRow3}</tr>
            </thead>
            <tbody>
              ${tableRowsHTML}
              <tr>${totalRow}</tr>
              <tr>${pctRow}</tr>
            </tbody>
          </table>
          
          <div class="footer">
            <div class="footer-left">NB: W = Written | O = Oral | LO Wise Achievement % = (Total Obtained / Max Possible) × 100</div>
            <div class="footer-right">Signature of HM: _______________________</div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.print();
    }, 500);
  };

  // Excel Export
  const handleExportExcel = () => {
    if (!reportData || reportData.students.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    try {
      const { meta, blueprint, students } = reportData;
      const loGroups = groupByLO(blueprint);
      
      const wsData = [
        [`SCHOOL LEVEL COMPILATION FORMAT FOR ${meta.exam} RESULT ANALYSIS ${meta.year}`],
        ['FORMAT - A'],
        [],
        ['DISTRICT:', meta.district.toUpperCase(), '', 'BLOCK:', meta.block.toUpperCase(), '', 'CLUSTER:', meta.cluster.toUpperCase()],
        ['SCHOOL:', meta.school.toUpperCase(), '', 'CLASS:', meta.class_name, '', 'SUBJECT:', meta.subject],
        [],
        ['SI.', 'Name of Student', ...loGroups.map(g => g.lo), 'Grand Total'],
        ['No.', '', ...loGroups.map(g => `W:${g.writtenMarks || '--'} | O:${g.oralMarks || '--'}`), `(${loGroups.reduce((s, g) => s + g.totalMarks, 0)})`]
      ];
      
      students.forEach((student, idx) => {
        let grandTotal = 0;
        const row = [idx + 1, student.name];
        loGroups.forEach(group => {
          const loTotal = getStudentLOTotal(student, group.items);
          grandTotal += loTotal;
          row.push(loTotal);
        });
        row.push(grandTotal);
        wsData.push(row);
      });

      // Total row
      const totalRow = ['', 'Total Marks Obtained'];
      let grandTotal = 0;
      loGroups.forEach(group => {
        const loTotal = getLOClassTotal(students, group.items);
        grandTotal += loTotal;
        totalRow.push(loTotal);
      });
      totalRow.push(grandTotal);
      wsData.push(totalRow);

      // Percentage row
      const pctRow = ['', 'LO Wise Achievement %'];
      const totalPossible = loGroups.reduce((s, g) => s + g.totalMarks, 0);
      loGroups.forEach(group => {
        const total = getLOClassTotal(students, group.items);
        const max = group.totalMarks * students.length;
        pctRow.push(max > 0 ? `${((total / max) * 100).toFixed(1)}%` : '0%');
      });
      const overallPct = (totalPossible * students.length) > 0 
        ? ((grandTotal / (totalPossible * students.length)) * 100).toFixed(1)
        : '0.0';
      pctRow.push(`${overallPct}%`);
      wsData.push(pctRow);

      const ws = XLSX.utils.aoa_to_sheet(wsData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Format-A');
      
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, `Format-A_${meta.class_name}_${meta.subject}_${meta.exam}_${meta.year}.xlsx`);
      
      toast.success('Excel exported successfully!');
    } catch (error) {
      console.error('Excel export error:', error);
      toast.error('Failed to export Excel');
    }
  };

  // WhatsApp Share
  const handleWhatsAppShare = () => {
    const message = encodeURIComponent(
      `🎓 *Teacher Utility Toolkit* - Generate Format-A Reports Instantly!\n\n` +
      `✅ Auto LO-wise % calculation\n` +
      `✅ Official Format-A PDF export\n` +
      `✅ Works for all subjects & classes\n\n` +
      `Try it now: ${window.location.origin}\n\n` +
      `📧 Contact: teacherutilitytoolkit@gmail.com`
    );
    window.open(`https://wa.me/?text=${message}`, '_blank');
  };

  if (loading) {
    return (
      <div className="p-4 md:p-6 lg:p-8 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-navy" />
      </div>
    );
  }

  if (entries.length === 0 || !reportData) {
    return (
      <div className="p-4 md:p-6 lg:p-8">
        <Card>
          <CardContent className="p-8 md:p-12 text-center">
            <Eye className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="font-medium text-slate-700 mb-2">No Report to Preview</h3>
            <p className="text-sm text-slate-500">Create a blueprint and add students first</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const loGroups = groupByLO(reportData.blueprint);
  const hasData = reportData.students.length > 0 && reportData.blueprint.length > 0;
  const totalPossibleMarks = loGroups.reduce((sum, g) => sum + g.totalMarks, 0);

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-heading text-xl md:text-2xl lg:text-3xl font-bold text-navy">Preview & Export</h1>
          <p className="text-slate-600 mt-1 text-sm md:text-base">Official Format-A report generation</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={handlePrint} disabled={!hasData} size="sm" data-testid="print-btn">
            <Printer className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Print</span>
          </Button>
          <Button onClick={handleExportPDF} disabled={!hasData} className="bg-navy hover:bg-navy-dark" size="sm" data-testid="export-pdf-btn">
            <FileText className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Format-A PDF</span>
          </Button>
          <Button onClick={handleExportExcel} disabled={!hasData} className="bg-emerald-600 hover:bg-emerald-700 text-white" size="sm" data-testid="export-excel-btn">
            <FileSpreadsheet className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Export Excel</span>
          </Button>
          <Button onClick={handleExportSummaryPDF} disabled={!hasData} className="bg-purple-600 hover:bg-purple-700 text-white" size="sm" data-testid="export-summary-btn">
            <FileText className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Summary PDF</span>
          </Button>
          <Button onClick={handleWhatsAppShare} className="bg-green-600 hover:bg-green-700 text-white" size="sm" data-testid="whatsapp-share-btn">
            <MessageCircle className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Share</span>
          </Button>
        </div>
      </div>

      {/* Entry selector */}
      <Card className="mb-4 md:mb-6">
        <CardContent className="p-3 md:p-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <span className="text-sm font-medium whitespace-nowrap">Select Report:</span>
            <select 
              className="flex-1 px-3 py-2 border rounded-md text-sm"
              value={selectedEntryId || ''}
              onChange={(e) => handleEntryChange(e.target.value)}
              data-testid="select-entry-preview"
            >
              {entries.map(entry => (
                <option key={entry.id} value={entry.id}>
                  Class {entry.class_name} - {entry.subject} ({entry.exam} {entry.year})
                </option>
              ))}
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Report preview - Official Format */}
      <Card>
        <CardHeader className="p-3 md:p-6">
          <CardTitle className="text-base md:text-lg flex items-center gap-2">
            <FileText className="w-4 h-4 md:w-5 md:h-5" />
            Official Format-A Preview
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          <div ref={printRef} className="p-4 md:p-6 min-w-[700px]" style={{ fontFamily: 'Calibri, Arial, sans-serif' }}>
            {/* Report Header - Official Format */}
            <div className="header text-center mb-4">
              <h1 className="text-sm md:text-base font-bold">SCHOOL LEVEL COMPILATION FORMAT FOR {reportData.meta.exam} RESULT ANALYSIS {reportData.meta.year}</h1>
              <h2 className="text-sm font-bold">FORMAT - A</h2>
            </div>
            
            <div className="grid grid-cols-3 gap-2 text-xs mb-4">
              <div><span className="font-bold">DISTRICT:</span> {reportData.meta.district.toUpperCase()}</div>
              <div><span className="font-bold">BLOCK:</span> {reportData.meta.block.toUpperCase()}</div>
              <div><span className="font-bold">CLUSTER:</span> {reportData.meta.cluster.toUpperCase()}</div>
              <div><span className="font-bold">SCHOOL:</span> {reportData.meta.school.toUpperCase()}</div>
              <div><span className="font-bold">CLASS:</span> {reportData.meta.class_name}</div>
              <div><span className="font-bold">SUBJECT:</span> {reportData.meta.subject}</div>
            </div>

            {hasData ? (
              <>
                {/* Report Table - Official Format with 3 header rows */}
                <table className="w-full text-[9px] md:text-[10px] border-collapse border border-black">
                  <thead>
                    {/* Row 1: LO Codes */}
                    <tr className="bg-gray-100">
                      <th rowSpan={3} className="border border-black px-1 py-1 w-8">SI.<br/>No.</th>
                      <th rowSpan={3} className="border border-black px-1 py-1 text-left min-w-[100px]">Name of Student</th>
                      {loGroups.map(group => (
                        <th key={group.lo} className="border border-black px-1 py-1 font-bold">
                          {group.lo}
                        </th>
                      ))}
                      <th rowSpan={3} className="border border-black px-1 py-1 bg-amber-50">
                        Grand<br/>Total<br/>({totalPossibleMarks})
                      </th>
                    </tr>
                    {/* Row 2: Question Numbers */}
                    <tr className="bg-gray-50 text-[7px]">
                      {loGroups.map(group => (
                        <th key={group.lo} className="border border-black px-1 py-0.5 font-normal">
                          {group.writtenQLabels !== '--' && <div>W: {group.writtenQLabels}</div>}
                          {group.oralQLabels !== '--' && <div>O: {group.oralQLabels}</div>}
                          {group.writtenQLabels === '--' && group.oralQLabels === '--' && '--'}
                        </th>
                      ))}
                    </tr>
                    {/* Row 3: Total Marks */}
                    <tr className="bg-gray-100 text-[8px]">
                      {loGroups.map(group => (
                        <th key={group.lo} className="border border-black px-1 py-0.5 font-bold">
                          W:{group.writtenMarks || '--'} | O:{group.oralMarks || '--'}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.students.map((student, idx) => {
                      let grandTotal = 0;
                      return (
                        <tr key={student.id} className={idx % 2 === 0 ? '' : 'bg-gray-50'}>
                          <td className="border border-black px-1 py-1 text-center">{idx + 1}</td>
                          <td className="border border-black px-1 py-1 text-left name-col truncate max-w-[120px]">{student.name}</td>
                          {loGroups.map(group => {
                            const loTotal = getStudentLOTotal(student, group.items);
                            grandTotal += loTotal;
                            return (
                              <td key={group.lo} className="border border-black px-1 py-1 text-center">
                                {loTotal}
                              </td>
                            );
                          })}
                          <td className="border border-black px-1 py-1 text-center font-bold bg-amber-50">
                            {grandTotal}
                          </td>
                        </tr>
                      );
                    })}
                    {/* Total Marks row */}
                    <tr className="bg-gray-100 font-bold">
                      <td colSpan={2} className="border border-black px-1 py-1 text-right">Total Marks</td>
                      {loGroups.map(group => (
                        <td key={group.lo} className="border border-black px-1 py-1 text-center">
                          {getLOClassTotal(reportData.students, group.items)}
                        </td>
                      ))}
                      <td className="border border-black px-1 py-1 text-center bg-amber-100">
                        {reportData.students.reduce((sum, s) => {
                          return sum + loGroups.reduce((t, g) => t + getStudentLOTotal(s, g.items), 0);
                        }, 0)}
                      </td>
                    </tr>
                    {/* LO Wise Achievement % row */}
                    <tr className="bg-emerald-50 font-bold">
                      <td colSpan={2} className="border border-black px-1 py-1 text-right">LO Wise Achievement %</td>
                      {loGroups.map(group => {
                        const total = getLOClassTotal(reportData.students, group.items);
                        const max = group.totalMarks * reportData.students.length;
                        const pct = max > 0 ? ((total / max) * 100).toFixed(1) : 0;
                        return (
                          <td key={group.lo} className="border border-black px-1 py-1 text-center text-emerald-700">
                            {pct}%
                          </td>
                        );
                      })}
                      <td className="border border-black px-1 py-1 text-center text-emerald-700">
                        {(() => {
                          const totalObtained = reportData.students.reduce((sum, s) => {
                            return sum + loGroups.reduce((t, g) => t + getStudentLOTotal(s, g.items), 0);
                          }, 0);
                          const maxTotal = totalPossibleMarks * reportData.students.length;
                          return maxTotal > 0 ? ((totalObtained / maxTotal) * 100).toFixed(1) : 0;
                        })()}%
                      </td>
                    </tr>
                  </tbody>
                </table>

                {/* Footer - NB on left, Signature on right */}
                <div className="footer mt-3 flex justify-between items-start text-[9px] text-gray-600">
                  <p className="max-w-[70%]">NB: W = Written | O = Oral | LO Wise Achievement % = (Total Obtained / Max Possible) × 100</p>
                  <p className="text-right">Signature of HM: _______________________</p>
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <p>No students or blueprint items found for this report.</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
